import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { updateCompany } from '../services/api';
import CompanyContestsModal from './CompanyContestsModal';

const CompanyTable = ({ companies, loading, onRefresh }) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isContestsModalOpen, setIsContestsModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState({
    name: '',
    description: '',
    website: '',
    email: '',
    ein: '',
    phone: '',
    active: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const handleEditClick = (company) => {
    setSelectedCompany(company);
    setCompanyForm({
      name: company.name || '',
      description: company.description || '',
      website: company.website || '',
      email: company.email || '',
      ein: company.ein || '',
      phone: company.phone || '',
      active: company.active !== undefined ? company.active : true
    });
    setFormErrors({});
    setIsEditModalOpen(true);
  };

  const handleContestsClick = (company) => {
    setSelectedCompany(company);
    setIsContestsModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
  };

  const closeContestsModal = () => {
    setIsContestsModalOpen(false);
  };

  const validateCompanyForm = () => {
    const errors = {};
    
    if (!companyForm.name) {
      errors.name = 'Name is required';
    }
    
    if (!companyForm.email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(companyForm.email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!companyForm.website) {
      errors.website = 'Website is required';
    }
/*    else if (!/^(http|https):\/\/[^ "]+$/.test(companyForm.website)) {
      errors.website = 'Website URL is invalid';
    }*/
    
    if (!companyForm.ein) {
      errors.ein = 'EIN is required';
    } else if (!/^\d{9}$/.test(companyForm.ein.replace(/\D/g, ''))) {
      errors.ein = 'EIN must be 9 digits';
    }
    
    if (!companyForm.phone) {
      errors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(companyForm.phone.replace(/\D/g, ''))) {
      errors.phone = 'Phone must be 10 digits';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };



  const handleCompanyFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCompanyForm({
      ...companyForm,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Clear error when typing
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };



  const handleSubmitCompanyUpdate = async (e) => {
    e.preventDefault();
    
    if (!validateCompanyForm()) return;
    
    setIsSubmitting(true);
    
    try {
      await updateCompany(selectedCompany.id, companyForm);
      toast.success('Sponsor information updated successfully');
      closeEditModal();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update sponsor information');
      console.error('Error updating company:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!companies || companies.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <p className="text-gray-500">No companies found. Try adjusting your filters.</p>
      </div>
    );
  }

  // Company Card component for mobile view
  const CompanyCard = ({ company }) => {
    return (
      <div className="bg-white rounded-lg shadow mb-4 overflow-hidden">
        <div className="p-4">
          {/* Company Profile Picture and Name */}
          <div className="flex items-center mb-4">
            {company.filePath ? (
              <img
                src={company.filePath}
                alt={`${company.name} profile`}
                className="w-12 h-12 rounded-full object-cover mr-3"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center mr-3">
                <span className="text-gray-600 text-lg font-medium">
                  {company.name ? company.name.charAt(0).toUpperCase() : 'C'}
                </span>
              </div>
            )}
            <h3 className="text-lg font-medium text-gray-900">{company.name}</h3>
          </div>
          
          <div className="space-y-3 mb-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Email:</p>
              <p className="text-sm text-gray-900 break-words">{company.email}</p>
            </div>
            
            <div>
              <p className="text-sm font-medium text-gray-500">Website:</p>
              <a 
                href={company.website} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-sm text-blue-600 hover:underline break-words"
              >
                {company.website}
              </a>
            </div>
            
            <div>
              <p className="text-sm font-medium text-gray-500">Phone:</p>
              <p className="text-sm text-gray-900">{company.phone || 'Not provided'}</p>
            </div>
            
            <div>
              <p className="text-sm font-medium text-gray-500">EIN:</p>
              <p className="text-sm text-gray-900">{company.ein || 'Not provided'}</p>
            </div>
            
            <div>
              <p className="text-sm font-medium text-gray-500">Status:</p>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                company.active 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {company.active ? 'Active' : 'Inactive'}
              </span>
            </div>
            
            <div>
              <p className="text-sm font-medium text-gray-500">Description:</p>
              <p className="text-sm text-gray-900">{company.description || 'No description available'}</p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2 mt-4">
            <button
              onClick={() => handleEditClick(company)}
              className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 min-h-[44px] text-sm"
            >
              Update Information
            </button>
            <button
              onClick={() => handleContestsClick(company)}
              className="px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 min-h-[44px] text-sm"
            >
              Contests
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Desktop view - Table */}
      <div className="hidden md:block overflow-x-auto bg-white rounded-lg shadow">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Profile</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sponsor Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">EIN</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Website</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {companies.map((company) => (
              <tr key={company.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  {company.filePath ? (
                    <img
                      src={company.filePath}
                      alt={`${company.name} profile`}
                      className="w-10 h-10 rounded-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className={`w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center ${company.filePath ? 'hidden' : 'flex'}`}>
                    <span className="text-gray-600 text-sm font-medium">
                      {company.name ? company.name.charAt(0).toUpperCase() : 'C'}
                    </span>
                  </div>
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{company.name}</div>
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{company.email}</div>
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{company.phone || 'Not provided'}</div>
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{company.ein || 'Not provided'}</div>
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    company.active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {company.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap">
                  <a 
                    href={company.website} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-sm text-blue-600 hover:underline"
                  >
                    {company.website}
                  </a>
                </td>
                
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900 max-w-xs truncate">{company.description || 'No description available'}</div>
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditClick(company)}
                      className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleContestsClick(company)}
                      className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                    >
                      Contests
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile view - Cards */}
      <div className="md:hidden">
        {companies.map((company) => (
          <CompanyCard key={company.id} company={company} />
        ))}
      </div>

      {/* Edit Company Modal */}
      {isEditModalOpen && selectedCompany && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-medium text-gray-900">
                Edit Sponsor Information
              </h3>
              <button
                onClick={closeEditModal}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleSubmitCompanyUpdate} className="p-4">
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                     Sponsor Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={companyForm.name}
                    onChange={handleCompanyFormChange}
                    className={`appearance-none rounded-md relative block w-full px-3 py-2 border ${
                      formErrors.name ? 'border-red-300' : 'border-gray-300'
                    } placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm`}
                  />
                  {formErrors.name && (
                    <p className="mt-2 text-sm text-red-600">{formErrors.name}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={companyForm.email}
                    onChange={handleCompanyFormChange}
                    className={`appearance-none rounded-md relative block w-full px-3 py-2 border ${
                      formErrors.email ? 'border-red-300' : 'border-gray-300'
                    } placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm`}
                  />
                  {formErrors.email && (
                    <p className="mt-2 text-sm text-red-600">{formErrors.email}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">
                    Website URL
                  </label>
                  <input
                    id="website"
                    name="website"
                    type="text"
                    value={companyForm.website}
                    onChange={handleCompanyFormChange}
                    className={`appearance-none rounded-md relative block w-full px-3 py-2 border ${
                      formErrors.website ? 'border-red-300' : 'border-gray-300'
                    } placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm`}
                  />
                  {formErrors.website && (
                    <p className="mt-2 text-sm text-red-600">{formErrors.website}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="ein" className="block text-sm font-medium text-gray-700 mb-1">
                    EIN
                  </label>
                  <input
                    id="ein"
                    name="ein"
                    type="text"
                    value={companyForm.ein}
                    onChange={handleCompanyFormChange}
                    className={`appearance-none rounded-md relative block w-full px-3 py-2 border ${
                      formErrors.ein ? 'border-red-300' : 'border-gray-300'
                    } placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm`}
                  />
                  {formErrors.ein && (
                    <p className="mt-2 text-sm text-red-600">{formErrors.ein}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="text"
                    value={companyForm.phone}
                    onChange={handleCompanyFormChange}
                    className={`appearance-none rounded-md relative block w-full px-3 py-2 border ${
                      formErrors.phone ? 'border-red-300' : 'border-gray-300'
                    } placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm`}
                  />
                  {formErrors.phone && (
                    <p className="mt-2 text-sm text-red-600">{formErrors.phone}</p>
                  )}
                </div>
                
                <div>
                  <div className="flex items-center">
                    <input
                      id="active"
                      name="active"
                      type="checkbox"
                      checked={companyForm.active}
                      onChange={handleCompanyFormChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="active" className="ml-2 block text-sm font-medium text-gray-700">
                      Active
                    </label>
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    Check this box to keep the company active in the system
                  </p>
                </div>
                
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    Sponsor Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    rows={3}
                    value={companyForm.description}
                    onChange={handleCompanyFormChange}
                    className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  />
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:col-start-2 sm:text-sm disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Updating...' : 'Update'}
                </button>
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Company Contests Modal */}
      <CompanyContestsModal
        isOpen={isContestsModalOpen}
        onClose={closeContestsModal}
        company={selectedCompany}
      />
    </div>
  );
};

export default CompanyTable; 